import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'es' | 'en';

const translations = {
  es: {
    nav: {
      home: 'Inicio',
      feed: 'Muro',
      pets: 'Mascotas',
      health: 'Salud IA',
      profile: 'Perfil',
      dev: 'Herramientas Pro',
      pro: 'Pro'
    },
    auth: {
      welcome: 'Bienvenido',
      loginSub: 'Inicia sesión en Amigo Peludo',
      email: 'Correo',
      password: 'Contraseña',
      enter: 'Ingresar',
      demo: 'Ingreso Demo (Datos Falsos)',
      noAccount: '¿No tienes cuenta?',
      registerLink: 'Regístrate aquí',
      createAccount: 'Crear Cuenta',
      join: 'Únete a la comunidad',
      fullName: 'Nombre Completo',
      rut: 'RUT / ID / SSN',
      phone: 'Teléfono Móvil',
      addressTitle: 'Ubicación',
      country: 'País',
      region: 'Región / Estado',
      city: 'Comuna / Ciudad',
      address: 'Calle y Número',
      addressDetail: 'Depto / Casa / Unidad (Opcional)',
      registerBtn: 'Registrarme',
      haveAccount: '¿Ya tienes cuenta?',
      loginLink: 'Inicia sesión aquí',
      locationError: 'Selecciona una ubicación válida',
      success: '¡Cuenta creada con éxito!',
      connectionError: 'Error de conexión con el servidor.'
    },
    dashboard: {
      myCompanions: 'Mis Compañeros',
      addPet: 'Añadir Mascota',
      syncing: 'Sincronizando Cloud...',
      emptyTitle: 'Tu manada está vacía',
      emptyDesc: 'Inicia sesión para ver a tus mascotas, gestionar sus vacunas y acceder al veterinario IA.',
      enterNow: 'Entrar Ahora',
      pendingAction: 'Acciones',
      pendingInterview: 'Entrevista Pendiente',
      completeProfile: 'Completar Perfil',
      healthBtn: 'Salud',
      // Ads
      vetAdTitle: 'Veterinario',
      vetAdSub: 'IA 3.0',
      vetAdDesc: 'Analiza síntomas, lee recetas y obtén diagnósticos preventivos en segundos.',
      vetAdBtn: 'Consultar Ahora',
      shopAdTitle: 'Seguridad',
      shopAdSub: 'Pasiva',
      shopAdDesc: 'Collares 3D indestructibles con QR de emergencia.',
      shopAdBtn: 'Ver Colección',
      newsTitle: 'Novedades',
      news1: 'Nueva alianza con Farmacias Veterinarias.',
      news2: 'Tips para el cuidado dental senior.'
    }
  },
  en: {
    nav: {
      home: 'Home',
      feed: 'Community',
      pets: 'My Pets',
      health: 'AI Vet',
      profile: 'Profile',
      dev: 'Dev Tools',
      pro: 'Pro'
    },
    auth: {
      welcome: 'Welcome',
      loginSub: 'Sign in to Amigo Peludo',
      email: 'Email',
      password: 'Password',
      enter: 'Sign In',
      demo: 'Demo Login (Fake Data)',
      noAccount: 'Don\'t have an account?',
      registerLink: 'Register here',
      createAccount: 'Create Account',
      join: 'Join the pack',
      fullName: 'Full Name',
      rut: 'ID / SSN',
      phone: 'Mobile Phone',
      addressTitle: 'Location',
      country: 'Country',
      region: 'Region / State',
      city: 'City / County',
      address: 'Street Address',
      addressDetail: 'Apt / Unit / Suite (Optional)',
      registerBtn: 'Sign Up',
      haveAccount: 'Already have an account?',
      loginLink: 'Log in here',
      locationError: 'Please select a valid location',
      success: 'Account created successfully!',
      connectionError: 'Server connection error.'
    },
    dashboard: {
      myCompanions: 'My Companions',
      addPet: 'Add Pet',
      syncing: 'Cloud Syncing...',
      emptyTitle: 'Your pack is empty',
      emptyDesc: 'Log in to manage your pets, track vaccines, and access the AI Veterinarian.',
      enterNow: 'Login Now',
      pendingAction: 'Actions',
      pendingInterview: 'Profile Pending',
      completeProfile: 'Complete Profile',
      healthBtn: 'Health',
      // Ads
      vetAdTitle: 'Veterinarian',
      vetAdSub: 'AI 3.0',
      vetAdDesc: 'Analyze symptoms, read prescriptions, and get preventive diagnostics in seconds.',
      vetAdBtn: 'Consult Now',
      shopAdTitle: 'Passive',
      shopAdSub: 'Security',
      shopAdDesc: 'Indestructible 3D collars with emergency QR.',
      shopAdBtn: 'View Collection',
      newsTitle: 'News',
      news1: 'New partnership with Veterinary Pharmacies.',
      news2: 'Tips for senior pet dental care.'
    }
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.es;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('es');

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
